import React, { useMemo } from 'react';
import { CSVLink } from "react-csv";
import * as dayjs from "dayjs";
import { TableInstance, Column } from 'react-table';
import Helper from '../helpers/Helper';

interface TableCSVExportProps {
    tableInstance: TableInstance<any>;
    filename: string;
    className?: string;
    buttonText?: string;
    excludeColumns?: string[]; // Column accessors to exclude from CSV
    transformData?: (data: any[]) => any[]; // Optional function to transform data before CSV export
    headers?: Array<{ label: string; key: string }>; // Optional custom headers for CSV export
    instanceTime?: number; // Instance time for calculating Weight % columns
}

/**
 * Reusable CSV export component for react-table tables.
 * 
 * Features:
 * - Exports data in the same sort order as the table
 * - Formats time fields using the same logic as table cells
 * - Automatically generates headers from column definitions
 * - Excludes hidden columns and specified columns
 * - Supports custom data transformations via transformData prop
 */
const TableCSVExport: React.FC<TableCSVExportProps> = ({
    tableInstance,
    filename,
    className = "btn btn-sm btn-primary",
    buttonText = "Export",
    excludeColumns = [],
    transformData,
    headers: customHeaders,
    instanceTime
}) => {
    const { rows, columns, state } = tableInstance;

    // Get sorted rows - rows are already sorted by react-table
    const sortedRows = rows;

    // Prepare CSV data with proper formatting
    const csvData = useMemo(() => {
        let data = sortedRows.map(row => {
            const rowData: any = {};
            
            // Store original row data for transform function access
            rowData._original = row.original;
            
            // Process each column
            columns.forEach((column: Column<any>) => {
                // Use column.id as the key (react-table uses id in row.values, not accessor)
                const columnId = column.id || (typeof column.accessor === 'string' ? column.accessor : '');
                
                if (!columnId) {
                    return;
                }
                
                const accessor = column.accessor;
                const accessorString = typeof accessor === 'string' ? accessor : '';
                
                // Skip hidden columns, excluded columns, and action columns
                // Action columns are identified by:
                // - accessor === 'actions' or id === 'actions'
                // - className or headerClassName === 'actions'
                const isActionColumn = 
                    accessorString === 'actions' ||
                    columnId === 'actions' ||
                    (column as any).className === 'actions' ||
                    (column as any).headerClassName === 'actions';
                
                // Check both id and accessor for hidden/excluded columns
                if (
                    state.hiddenColumns?.includes(columnId) ||
                    state.hiddenColumns?.includes(accessorString) ||
                    excludeColumns.includes(columnId) ||
                    excludeColumns.includes(accessorString) ||
                    isActionColumn
                ) {
                    return;
                }

                // Get the raw value using column.id (react-table uses id as key in row.values)
                let value = row.values[columnId];
                
                // Handle Weight % column calculation (calculated column, not stored in data)
                if ((columnId === 'weight' || accessorString === 'weight') && instanceTime !== undefined) {
                    const waittime = row.values['waittime'];
                    if (waittime !== null && waittime !== undefined && instanceTime > 0) {
                        const percentage = (100 / instanceTime) * waittime;
                        value = `${Helper.formatPercentage(percentage)}%`;
                    }
                }
                
                // Check if this is a time-related field (check both id and accessor)
                const idLower = columnId.toLowerCase();
                const accessorLower = accessorString.toLowerCase();
                const isTimeField = idLower.includes('time') || 
                                   idLower.includes('duration') ||
                                   idLower.includes('waittime') ||
                                   idLower.includes('averagetime') ||
                                   idLower.includes('timeslice') ||
                                   accessorLower.includes('time') || 
                                   accessorLower.includes('duration') ||
                                   accessorLower.includes('waittime') ||
                                   accessorLower.includes('averagetime') ||
                                   accessorLower.includes('timeslice');
                
                // Format time fields
                if (isTimeField && typeof value === 'number') {
                    // Format time using Helper.getTimeInEnglish (same as table display)
                    value = Helper.getTimeInEnglish(value);
                } else if (isTimeField && (typeof value === 'string' || value instanceof Date)) {
                    // Format date/time strings using dayjs (same format as table)
                    try {
                        value = dayjs.default(value).format('YYYY-MM-DD HH:mm:ss');
                    } catch (e) {
                        // Keep original value if parsing fails
                    }
                } else if (typeof value === 'boolean') {
                    value = value ? 'Yes' : 'No';
                } else if (Array.isArray(value)) {
                    // Handle arrays - convert to length for instances column, or comma-separated string
                    if (columnId === 'instances' || accessorString === 'instances') {
                        value = value.length.toString();
                    } else {
                        // For other arrays, join with comma or show length
                        value = value.length > 0 ? value.length.toString() : '';
                    }
                } else if (typeof value === 'object' && value !== null) {
                    // Handle objects - convert to JSON string or empty string
                    try {
                        value = JSON.stringify(value);
                    } catch (e) {
                        value = '';
                    }
                }
                
                // Handle null/undefined values
                if (value === null || value === undefined) {
                    value = '';
                }
                
                // Use columnId as the key in rowData
                rowData[columnId] = value;
            });
            
            return rowData;
        });

        // Apply custom transformation if provided
        // Transform function receives CSV-ready data with _original property for accessing original row data
        if (transformData) {
            data = transformData(data);
        }

        // Remove _original property before returning (it's only for transform function use)
        // This ensures _original is available during transform but not in final CSV
        return data.map((row: any) => {
            if (row._original) {
                const { _original, ...csvRow } = row;
                return csvRow;
            }
            return row;
        });
    }, [sortedRows, columns, state.hiddenColumns, excludeColumns, transformData, instanceTime]);

    // Generate CSV headers from column definitions or use custom headers
    const csvHeaders = useMemo(() => {
        // If custom headers are provided, use them
        if (customHeaders && customHeaders.length > 0) {
            return customHeaders;
        }
        
        // Otherwise, generate headers from column definitions
        return columns
            .filter((column: Column<any>) => {
                // Use column.id as the key (react-table uses id in row.values)
                const columnId = column.id || (typeof column.accessor === 'string' ? column.accessor : '');
                
                if (!columnId) {
                    return false;
                }
                
                const accessor = column.accessor;
                const accessorString = typeof accessor === 'string' ? accessor : '';
                
                // Exclude action columns automatically
                const isActionColumn = 
                    accessorString === 'actions' ||
                    columnId === 'actions' ||
                    (column as any).className === 'actions' ||
                    (column as any).headerClassName === 'actions';
                
                // Check both id and accessor for hidden/excluded columns
                return (
                    !state.hiddenColumns?.includes(columnId) &&
                    !state.hiddenColumns?.includes(accessorString) &&
                    !excludeColumns.includes(columnId) &&
                    !excludeColumns.includes(accessorString) &&
                    !isActionColumn
                );
            })
            .map((column: Column<any>) => {
                const columnId = column.id || (typeof column.accessor === 'string' ? column.accessor : '');
                const accessor = column.accessor;
                
                // Handle Header that might be a string, function, or React element
                let headerLabel: string;
                if (typeof column.Header === 'string') {
                    headerLabel = column.Header;
                } else if (typeof column.Header === 'function') {
                    // Try to get a readable label from the accessor or id
                    if (typeof accessor === 'string') {
                        headerLabel = accessor.charAt(0).toUpperCase() + accessor.slice(1).replace(/([A-Z])/g, ' $1');
                    } else {
                        // Fallback: use column id or a default label
                        headerLabel = columnId || 'Column';
                    }
                } else {
                    // Default: use accessor as string or column id
                    if (typeof accessor === 'string') {
                        headerLabel = accessor;
                    } else {
                        headerLabel = columnId || 'Column';
                    }
                }
                
                // Use columnId as the key (must match the key used in csvData)
                return {
                    label: headerLabel,
                    key: columnId
                };
            });
    }, [columns, state.hiddenColumns, excludeColumns, customHeaders]);

    // Generate filename with timestamp
    const downloadFilename = useMemo(() => {
        const timestamp = dayjs.default().format('YYYY-MM-DD HH:mm');
        return `DBmarlin - ${timestamp} - ${filename}.csv`;
    }, [filename]);

    return (
        <CSVLink
            role="button"
            headers={csvHeaders}
            data={csvData}
            filename={downloadFilename}
            className={className}
        >
            <i className="fal fa-file-export fa-fw"/><span>{buttonText}</span>
        </CSVLink>
    );
};

export default TableCSVExport;
